import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, FileText, ShieldCheck, AlertTriangle, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/common/DataTable";
import { TablaAmortizacionView } from "@/components/creditos/TablaAmortizacionView";
import { ReciboPagoButton } from "@/components/common/ReciboPagoButton";
import { mxn } from "@/lib/format";
import { esCreditoFactoraje, plazoCredito } from "@/lib/factoraje";
import { productoCredito } from "@/lib/creditos";
import { FactorajeMovimientos } from "@/components/cliente/FactorajeMovimientos";
import { esCreditoArrendamiento, SISTEMA_ARRENDAMIENTO } from "@/lib/arrendamiento";

/** Renderiza el valor de un dato_credito según su tipo definido en `campos_datos_def`. */
function renderValor(valor, tipo) {
  if (valor === undefined || valor === null || valor === "") return <span className="text-slate-400">—</span>;
  if (tipo === "number") {
    const n = Number(valor);
    if (!Number.isFinite(n)) return String(valor);
    // Si el label sugiere un monto (valor_avaluo, valor_factura, monto_...), formatear MXN.
    return n >= 10000 ? mxn(n) : n.toLocaleString("es-MX");
  }
  if (tipo === "date") {
    try { return new Date(valor).toLocaleDateString("es-MX"); } catch { return String(valor); }
  }
  return String(valor);
}

// Acción por-fila para descargar recibo de pago; declarado a nivel de módulo para evitar
// re-crear el componente en cada render (react/no-unstable-nested-components).
const reciboPagoAction = (p) => <ReciboPagoButton pago={p} size="icon" />;

function BadgeEstatus({ credito }) {
  const dias = Number(credito.dias_atraso || 0);
  const est = credito.estatus_operacion || credito.estatus || "—";
  if (dias > 0) {
    return <Badge className="bg-rose-100 text-rose-700 border-rose-200">{dias} día{dias === 1 ? "" : "s"} atraso</Badge>;
  }
  if (est === "Vigente" || est === "Al corriente") {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Al corriente</Badge>;
  }
  return <Badge variant="outline">{est}</Badge>;
}

export function CreditoCard({ credito, pagos, garantia, testid, perfilClienteId }) {
  const [expanded, setExpanded] = useState(false);
  const [tablaVisible, setTablaVisible] = useState(false); // lazy-load
  const pagosDelCredito = useMemo(
    () => (pagos || []).filter((p) => p.credito_id === credito.id),
    [pagos, credito.id],
  );
  // Fase A.1 (feb-2026) — Cuando la tarjeta se abre desde el 360 de un CLIENTE que es
  // integrante de un crédito grupal, mostramos la PORCIÓN del integrante (no la raíz).
  // El backend sella `comision_integrante`, `iva_comision_integrante` y
  // `monto_ministrado_integrante` por miembro en `credito.integrantes[]`.
  const miIntegrante = useMemo(() => {
    if (!perfilClienteId || !credito?.es_grupal) return null;
    return (credito.integrantes || []).find((i) => i.cliente_id === perfilClienteId) || null;
  }, [perfilClienteId, credito]);
  const camposDef = credito.campos_datos_def || [];
  const datos = credito.datos_credito || {};
  const tabla = credito.tabla_amortizacion || [];
  // Arrendamiento: la fila técnica del residual no cuenta como cuota (las demás tablas no la traen).
  const cuotas = tabla.filter((c) => !c.es_residual);
  const cuotasPagadas = cuotas.filter((c) => c.estatus === "Pagada").length;
  const cuotasTotales = cuotas.length;
  const proximaCuota = tabla.find((c) => c.estatus !== "Pagada");
  // Diferimiento (jun-2026): moratorio congelado + GC diferidos pendientes. El "próximo pago" debe
  // reflejar el TOTAL a cobrar (cuota + diferidos), no solo la cuota — consistencia con Cartera/Cobranza.
  const cuotaProxima = proximaCuota ? Number(proximaCuota.pago_total || proximaCuota.pago || proximaCuota.total || 0) : 0;
  const difMoraPend = Number(credito.total_moratorios_diferidos_pendientes || 0);
  const difGcPend = Number(credito.total_gastos_cobranza_diferidos_pendientes || 0);
  const totalProximoPago = Math.round((cuotaProxima + difMoraPend + difGcPend) * 100) / 100;
  const hayDiferidosPend = difMoraPend + difGcPend > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      {/* Header (siempre visible, clickeable para expandir) */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
        data-testid={`${testid}-toggle`}
      >
        {expanded ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
        <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono text-sm font-semibold text-slate-900">{credito.numero_credito || credito.id?.slice(0, 8)}</span>
          <Badge variant="secondary">{productoCredito(credito)}</Badge>
          {credito.relacion_grupal === "obligado_solidario" && (
            <Link
              to={`/r/creditos?id=${credito.id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
              data-testid={`obligado-solidario-badge-${credito.id}`}
              title="Este cliente figura como integrante no titular. La obligación es solidaria conforme al Art. 95 Bis LGOAAC."
            >
              <ShieldCheck className="h-3 w-3" />
              Obligado solidario
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          {/* Fase 5 (feb-2026) — Badge de línea/disposición. Solo renderiza cuando el crédito
              es DISPOSICIÓN (linea_id presente). Un crédito simple NO ve nada, cero regresión. */}
          {credito.linea_id && credito.linea_numero && (
            <Link
              to={`/lineas/${credito.linea_id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
              data-testid={`c360-badge-linea-${credito.id}`}
              title={`Abrir línea ${credito.linea_numero}`}
            >
              <Wallet className="h-3 w-3" />
              {credito.linea_numero}
              {credito.disposicion_numero != null && (
                <span className="text-emerald-500">· D{String(credito.disposicion_numero).padStart(2, "0")}</span>
              )}
              <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          <span className="text-sm text-slate-600">Saldo: <span className="font-semibold text-slate-900">{mxn(credito.saldo_insoluto || 0)}</span></span>
          <BadgeEstatus credito={credito} />
          {credito.remanente && (
            <span className="text-sm text-slate-600" data-testid={`${testid}-remanente-pendiente`}>
              Remanente pendiente: <span className="font-semibold text-amber-700">{mxn(credito.remanente.pendiente || 0)}</span>
            </span>
          )}
        </div>
      </button>

      {/* Cuerpo (expandible) */}
      {expanded && (
        <div className="border-t border-slate-200 px-4 py-4">
          {/* Sub-bloque 1: Datos del producto (campos_datos según tipo) */}
          <section className="mb-5">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Datos del producto</h4>
            {camposDef.length === 0 ? (
              <p className="text-xs text-slate-400">Este tipo de crédito no captura datos específicos adicionales.</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {camposDef.map((cf) => (
                  <div key={cf.clave} className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={`${testid}-dc-${cf.clave}`}>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{cf.label}</p>
                    <p className="mt-0.5 text-sm font-medium text-slate-800 break-words">{renderValor(datos[cf.clave], cf.tipo)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Sub-bloque 2: Estado y avance */}
          <section className="mb-5">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Estado y avance</h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              <StatBox label="Plazo" value={plazoCredito(credito)} testid={`${testid}-plazo`} />
              <StatBox label="Cuotas pagadas" value={cuotasTotales > 0 ? `${cuotasPagadas} / ${cuotasTotales}` : "—"} testid={`${testid}-cuotas`} />
              <StatBox
                label={hayDiferidosPend ? "Próximo pago (total)" : "Próximo pago"}
                value={proximaCuota
                  ? (hayDiferidosPend
                      ? <span>{proximaCuota.fecha_pago} · <b>{mxn(totalProximoPago)}</b>
                          <span className="ml-1 block text-[10px] font-normal text-slate-500">
                            Cuota {mxn(cuotaProxima)} + diferidos {mxn(difMoraPend + difGcPend)}
                          </span>
                        </span>
                      : `${proximaCuota.fecha_pago} · ${mxn(cuotaProxima)}`)
                  : "—"}
                testid={`${testid}-proximo`} />
              <StatBox label="Monto vencido" value={mxn(credito.monto_vencido || 0)} testid={`${testid}-vencido`} />
              <StatBox label="Días de atraso" value={String(credito.dias_atraso || 0)} testid={`${testid}-atraso`} />
              {/* Bloque 6 (feb 2026): gastos de cobranza acumulados PENDIENTES si el crédito
                  activó cargos por gestiones efectivas. Solo se muestra si hay algo pendiente
                  o cargos históricos. Motor y tabla NO cambian — es solo visualización 360. */}
              {(Number(credito.total_gastos_cobranza_pendientes || 0) > 0
                || (credito.cargos_cobranza || []).length > 0) && (
                <StatBox
                  label="Gastos de cobranza acum."
                  value={mxn(credito.total_gastos_cobranza_pendientes || 0)}
                  testid={`${testid}-gc-acumulado`} />
              )}
              {/* Diferimiento (jun-2026): moratorio congelado pendiente de cobro */}
              {Number(credito.total_moratorios_diferidos_pendientes || 0) > 0 && (
                <StatBox
                  label="Moratorio diferido (congelado)"
                  value={mxn(credito.total_moratorios_diferidos_pendientes)}
                  testid={`${testid}-mora-diferida`} />
              )}
              <StatBox label="CAT" value={credito.cat !== undefined && credito.cat !== null ? `${credito.cat}%` : "—"} testid={`${testid}-cat`} />
              {miIntegrante ? (
                <>
                  <StatBox
                    label="Comisión apertura (tu porción)"
                    value={mxn(miIntegrante.comision_integrante || 0)}
                    testid={`${testid}-comision-integrante`}
                  />
                  <StatBox
                    label="IVA comisión (tu porción)"
                    value={mxn(miIntegrante.iva_comision_integrante || 0)}
                    testid={`${testid}-iva-comision-integrante`}
                  />
                  <StatBox
                    label="Neto a ministrar (tu porción)"
                    value={mxn(miIntegrante.monto_ministrado_integrante || 0)}
                    testid={`${testid}-neto-integrante`}
                  />
                </>
              ) : (
                <>
                  <StatBox label="Comisión apertura" value={mxn(credito.comision || 0)} testid={`${testid}-comision`} />
                  <StatBox label="IVA (total)" value={mxn(credito.iva || 0)} testid={`${testid}-iva`} />
                </>
              )}
              <StatBox label="Sistema" value={esCreditoArrendamiento(credito) ? SISTEMA_ARRENDAMIENTO
                : credito.tipo_amortizacion === "alemana" ? "Alemán (capital fijo)" : "Francés (cuota fija)"} testid={`${testid}-sistema`} />
            </div>
            {garantia && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid={`${testid}-garantia`}>
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <span className="font-semibold">Garantía asociada:</span> {garantia.tipo_garantia} — valor {mxn(garantia.valor_garantia || 0)}
                  {garantia.descripcion ? <span className="text-slate-600"> · {garantia.descripcion}</span> : null}
                </div>
              </div>
            )}
          </section>

          {/* Sub-bloque 3: Tabla de amortización (lazy-load) */}
          <section className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">Tabla de amortización{credito.plazo ? ` — ${plazoCredito(credito)}` : ""}</h4>
              <Button
                variant="outline" size="sm"
                onClick={() => setTablaVisible((v) => !v)}
                data-testid={`${testid}-tabla-toggle`}
              >
                {tablaVisible ? "Ocultar tabla" : `Ver tabla (${cuotasTotales} cuota${cuotasTotales === 1 ? "" : "s"})`}
              </Button>
            </div>
            {tablaVisible && (
              tabla.length === 0 ? (
                <p className="text-xs text-slate-400">Este crédito aún no tiene tabla de amortización.</p>
              ) : (
                <div data-testid={`${testid}-tabla-view`}>
                  <TablaAmortizacionView credito={credito} />
                </div>
              )
            )}
          </section>

          {esCreditoFactoraje(credito) && credito.movimientos_factoraje && (
            <FactorajeMovimientos credito={credito} testid={`${testid}-fx`} />
          )}

          {/* Sub-bloque 4: Pagos de este crédito (filtrados por credito_id) */}
          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Pagos aplicados a este crédito</h4>
            <DataTable
              rows={pagosDelCredito}
              emptyLabel="Sin pagos registrados para este crédito"
              columns={[
                { key: "recibo", label: "Recibo" },
                { key: "monto", label: "Monto", kind: "currency" },
                { key: "fecha_pago", label: "Fecha", kind: "date" },
                { key: "estatus", label: "Estatus", kind: "badge" },
              ]}
              actions={reciboPagoAction}
            />
          </section>

          {/* Enlaces cruzados */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <Link to={`/sofom/cartera?credito=${credito.numero_credito || credito.id}`}>
              <Button variant="outline" size="sm" data-testid={`${testid}-ver-cartera`}>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Ver en Cartera
              </Button>
            </Link>
            <Link to={`/r/expedientes?credito=${credito.id}`}>
              <Button variant="outline" size="sm" data-testid={`${testid}-ver-docs`}>
                <FileText className="mr-1.5 h-3.5 w-3.5" /> Documentos del crédito
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

/** Sección para pagos legacy sin credito_id: mostrarlos en vez de perderlos (defensa del audit). */
export function PagosSinCredito({ pagos }) {
  const huérfanos = useMemo(() => (pagos || []).filter((p) => !p.credito_id), [pagos]);
  if (huérfanos.length === 0) return null;
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="c360-pagos-huerfanos-box">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" /> Pagos sin crédito asociado (legacy)
      </div>
      <p className="mb-2 text-xs text-amber-800">
        Estos {huérfanos.length} pago{huérfanos.length === 1 ? "" : "s"} no tienen un crédito ligado (registro anterior al esquema actual). Se muestran para no perderse.
      </p>
      <DataTable
        rows={huérfanos}
        emptyLabel=""
        columns={[
          { key: "recibo", label: "Recibo" },
          { key: "monto", label: "Monto", kind: "currency" },
          { key: "fecha_pago", label: "Fecha", kind: "date" },
          { key: "estatus", label: "Estatus", kind: "badge" },
        ]}
        actions={reciboPagoAction}
      />
    </div>
  );
}

export default CreditoCard;
